/**
 * Ingredient Knowledge Base
 * 
 * Maps active ingredients to the skin concerns they address.
 * Used to enhance product-profile matching beyond simple string matching.
 * 
 * @module ingredientKnowledge
 */

// ============================================================================
// Ingredient → Concerns Mapping
// ============================================================================

/**
 * Maps active ingredients to their target skin concerns.
 * Each ingredient can address multiple concerns with different efficacy levels.
 * 
 * Structure: ingredient → { concerns: string[], efficacy: 'high' | 'medium' | 'low' }
 */
export const INGREDIENT_CONCERN_MAP: Record<string, { 
  concerns: string[]; 
  efficacy: 'high' | 'medium' | 'low';
  category: 'active' | 'hydrating' | 'soothing' | 'protective' | 'exfoliating';
  koreanTerms: string[]; // Korean concern translations
}> = {
  // === ANTI-ACNE / OIL CONTROL ===
  'salicylic acid': {
    concerns: ['acne', 'pores', 'blackheads', 'oiliness', 'blemishes', 'congestion'],
    efficacy: 'high',
    category: 'exfoliating',
    koreanTerms: ['여드름', '모공', '블랙헤드', '피지', '트러블']
  },
  'niacinamide': {
    concerns: ['pores', 'oiliness', 'redness', 'dark spots', 'uneven tone', 'acne scars'],
    efficacy: 'high',
    category: 'active',
    koreanTerms: ['모공', '피지', '붉은기', '잡티', '피부톤', '여드름 흉터']
  },
  'witch hazel': {
    concerns: ['pores', 'oiliness', 'inflammation'],
    efficacy: 'medium',
    category: 'soothing',
    koreanTerms: ['모공', '피지', '염증']
  },
  'betaine': {
    concerns: ['oiliness', 'hydration', 'irritation'],
    efficacy: 'medium',
    category: 'hydrating',
    koreanTerms: ['피지', '수분', '자극']
  },

  // === HYDRATION ===
  'hyaluronic acid': {
    concerns: ['dryness', 'dehydration', 'fine lines', 'plumpness', 'hydration'],
    efficacy: 'high',
    category: 'hydrating',
    koreanTerms: ['건조함', '수분 부족', '잔주름', '탄력', '수분']
  },
  'glycerin': {
    concerns: ['dryness', 'hydration', 'barrier'],
    efficacy: 'medium',
    category: 'hydrating',
    koreanTerms: ['건조함', '수분', '장벽']
  },
  'panthenol': {
    concerns: ['dryness', 'irritation', 'barrier', 'redness', 'sensitivity'],
    efficacy: 'high',
    category: 'soothing',
    koreanTerms: ['건조함', '자극', '장벽', '붉은기', '민감']
  },
  'squalane': {
    concerns: ['dryness', 'barrier', 'hydration'],
    efficacy: 'medium',
    category: 'hydrating',
    koreanTerms: ['건조함', '장벽', '수분']
  },

  // === BRIGHTENING ===
  'ascorbic acid': {
    concerns: ['dark spots', 'dullness', 'hyperpigmentation', 'uneven tone', 'brightening', 'sun damage'],
    efficacy: 'high',
    category: 'active',
    koreanTerms: ['잡티', '칙칙함', '색소침착', '피부톤', '미백', '광노화']
  },
  'ferulic acid': {
    concerns: ['sun damage', 'antioxidant', 'brightening'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['광노화', '항산화', '미백']
  },
  'galactomyces': {
    concerns: ['dullness', 'uneven tone', 'texture', 'hydration'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['칙칙함', '피부톤', '피부결', '수분']
  },
  'lactic acid': {
    concerns: ['dullness', 'texture', 'dark spots', 'hydration', 'exfoliation'],
    efficacy: 'medium',
    category: 'exfoliating',
    koreanTerms: ['칙칙함', '피부결', '잡티', '수분', '각질']
  },
  'glycolic acid': {
    concerns: ['dullness', 'texture', 'fine lines', 'acne scars', 'exfoliation'],
    efficacy: 'high',
    category: 'exfoliating',
    koreanTerms: ['칙칙함', '피부결', '잔주름', '여드름 흉터', '각질']
  },

  // === ANTI-AGING ===
  'retinol': {
    concerns: ['wrinkles', 'fine lines', 'aging', 'texture', 'dark spots', 'firmness'],
    efficacy: 'high',
    category: 'active',
    koreanTerms: ['주름', '잔주름', '노화', '피부결', '잡티', '탄력']
  },
  'bakuchiol': {
    concerns: ['wrinkles', 'fine lines', 'aging', 'firmness'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['주름', '잔주름', '노화', '탄력']
  },
  'peptides': {
    concerns: ['wrinkles', 'fine lines', 'firmness', 'elasticity', 'aging'],
    efficacy: 'high',
    category: 'active',
    koreanTerms: ['주름', '잔주름', '탄력', '탄성', '노화']
  },
  'adenosine': {
    concerns: ['wrinkles', 'fine lines', 'firmness'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['주름', '잔주름', '탄력']
  },

  // === BARRIER / SOOTHING ===
  'ceramides': {
    concerns: ['barrier', 'dryness', 'sensitivity', 'irritation', 'redness'],
    efficacy: 'high',
    category: 'soothing',
    koreanTerms: ['장벽', '건조함', '민감', '자극', '붉은기']
  },
  'cholesterol': {
    concerns: ['barrier', 'dryness'],
    efficacy: 'medium',
    category: 'soothing',
    koreanTerms: ['장벽', '건조함']
  },
  'fatty acids': {
    concerns: ['barrier', 'dryness', 'nourishment'],
    efficacy: 'medium',
    category: 'soothing',
    koreanTerms: ['장벽', '건조함', '영양']
  },
  'centella': {
    concerns: ['redness', 'sensitivity', 'irritation', 'acne scars', 'healing'],
    efficacy: 'high',
    category: 'soothing',
    koreanTerms: ['붉은기', '민감', '자극', '여드름 흉터', '진정']
  },
  'centella asiatica': {
    concerns: ['redness', 'sensitivity', 'irritation', 'acne scars', 'healing'],
    efficacy: 'high',
    category: 'soothing',
    koreanTerms: ['붉은기', '민감', '자극', '여드름 흉터', '진정']
  },
  'allantoin': {
    concerns: ['irritation', 'sensitivity', 'healing', 'soothing'],
    efficacy: 'medium',
    category: 'soothing',
    koreanTerms: ['자극', '민감', '진정', '수딩']
  },
  'green tea': {
    concerns: ['antioxidant', 'oiliness', 'inflammation', 'redness'],
    efficacy: 'medium',
    category: 'soothing',
    koreanTerms: ['항산화', '피지', '염증', '붉은기']
  },

  // === SUN PROTECTION ===
  'zinc oxide': {
    concerns: ['sun protection', 'sensitivity', 'redness'],
    efficacy: 'high',
    category: 'protective',
    koreanTerms: ['자외선 차단', '민감', '붉은기']
  },
  'titanium dioxide': {
    concerns: ['sun protection', 'sensitivity'],
    efficacy: 'high',
    category: 'protective',
    koreanTerms: ['자외선 차단', '민감']
  },
  'tinosorb s': {
    concerns: ['sun protection'],
    efficacy: 'high',
    category: 'protective',
    koreanTerms: ['자외선 차단']
  },
  'uva filters': {
    concerns: ['sun protection', 'sun damage'],
    efficacy: 'high',
    category: 'protective',
    koreanTerms: ['자외선 차단', '광노화']
  },

  // === HESEDA-SPECIFIC INGREDIENTS ===
  'glutathione': {
    concerns: ['brightening', 'dark spots', 'antioxidant', 'uneven tone'],
    efficacy: 'high',
    category: 'active',
    koreanTerms: ['미백', '잡티', '항산화', '피부톤']
  },
  'tocopherol': {
    concerns: ['antioxidant', 'hydration', 'barrier', 'sun damage'],
    efficacy: 'medium',
    category: 'protective',
    koreanTerms: ['항산화', '수분', '장벽', '광노화']
  },
  'beta-glucan': {
    concerns: ['hydration', 'soothing', 'barrier', 'sensitivity', 'redness'],
    efficacy: 'medium',
    category: 'soothing',
    koreanTerms: ['수분', '진정', '장벽', '민감', '붉은기']
  },
  'sodium hyaluronate': {
    concerns: ['dryness', 'dehydration', 'fine lines', 'hydration'],
    efficacy: 'high',
    category: 'hydrating',
    koreanTerms: ['건조함', '수분 부족', '잔주름', '수분']
  },
  'protopanaxadiol': {
    concerns: ['aging', 'firmness', 'wrinkles', 'brightening', 'antioxidant'],
    efficacy: 'high',
    category: 'active',
    koreanTerms: ['노화', '탄력', '주름', '미백', '항산화']
  },
  'ginseng': {
    concerns: ['aging', 'firmness', 'dullness', 'fatigue', 'hydration'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['노화', '탄력', '칙칙함', '피로', '수분']
  },
  'madecassoside': {
    concerns: ['redness', 'sensitivity', 'irritation', 'barrier', 'healing'],
    efficacy: 'high',
    category: 'soothing',
    koreanTerms: ['붉은기', '민감', '자극', '장벽', '진정']
  },
  'ascorbyl glucoside': {
    concerns: ['brightening', 'dark spots', 'uneven tone'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['미백', '잡티', '피부톤']
  },
  'atelocollagen': {
    concerns: ['firmness', 'elasticity', 'hydration', 'wrinkles'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['탄력', '탄성', '수분', '주름']
  },
  'hydrolyzed collagen': {
    concerns: ['firmness', 'elasticity', 'wrinkles', 'hydration'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['탄력', '탄성', '주름', '수분']
  },
  'hydrolyzed elastin': {
    concerns: ['firmness', 'elasticity', 'wrinkles'],
    efficacy: 'medium',
    category: 'active',
    koreanTerms: ['탄력', '탄성', '주름']
  },
  'citric acid': {
    concerns: ['exfoliation', 'brightening', 'texture'],
    efficacy: 'low',
    category: 'exfoliating',
    koreanTerms: ['각질', '미백', '피부결']
  },
};

// ============================================================================
// Concern Aliases (for semantic matching)
// ============================================================================

/**
 * Maps various ways users might describe concerns to standardized terms.
 * Supports both English and Korean input.
 */
export const CONCERN_ALIASES: Record<string, string[]> = {
  // Acne-related
  'acne': ['breakouts', 'pimples', 'zits', 'blemishes', 'spots', '여드름', '트러블', '뾰루지'],
  'pores': ['large pores', 'visible pores', 'open pores', '모공', '넓은 모공'],
  'blackheads': ['clogged pores', 'sebaceous filaments', '블랙헤드', '코 블랙헤드'],
  'oiliness': ['oily skin', 'excess sebum', 'shine', 'greasy', '피지', '기름', '번들거림', '유분'],
  
  // Dryness-related
  'dryness': ['dry skin', 'flaky', 'tight skin', 'rough', '건조함', '건조', '당김', '각질'],
  'dehydration': ['dehydrated', 'lacks moisture', 'thirsty skin', '수분 부족', '탈수'],
  'hydration': ['moisture', 'moisturizing', '수분', '보습'],
  
  // Aging-related
  'wrinkles': ['fine lines', 'lines', 'crow\'s feet', '주름', '잔주름'],
  'aging': ['anti-aging', 'mature skin', 'sagging', '노화', '안티에이징', '처짐'],
  'firmness': ['elasticity', 'bouncy', 'tight', 'lifting', '탄력', '탄성', '리프팅'],
  
  // Pigmentation-related
  'dark spots': ['hyperpigmentation', 'sun spots', 'age spots', 'melasma', '잡티', '색소침착', '기미'],
  'dullness': ['dull skin', 'lack of radiance', 'tired looking', '칙칙함', '광채 부족'],
  'uneven tone': ['discoloration', 'uneven skin tone', '피부톤', '톤 불균형'],
  
  // Sensitivity-related
  'redness': ['red skin', 'flushing', 'rosacea', '붉은기', '홍조'],
  'sensitivity': ['sensitive skin', 'reactive', 'easily irritated', '민감', '민감성'],
  'irritation': ['irritated', 'inflamed', '자극', '염증'],
  
  // Barrier-related
  'barrier': ['skin barrier', 'compromised barrier', 'damaged barrier', '장벽', '피부장벽'],
};

// ============================================================================
// Scoring Functions
// ============================================================================

/**
 * Calculates ingredient-based score boost for a product based on user concerns.
 * 
 * @param productIngredients - Array of ingredient names from the product
 * @param userConcerns - Array of user's skin concerns
 * @returns Score boost (0 to 1.0) and matched ingredients with reasons
 */
export function calculateIngredientScore(
  productIngredients: string[],
  userConcerns: string[]
): {
  scoreBoost: number;
  matchedIngredients: Array<{ ingredient: string; matchedConcerns: string[]; efficacy: string }>;
} {
  const matchedIngredients: Array<{ ingredient: string; matchedConcerns: string[]; efficacy: string }> = [];
  let totalBoost = 0;
  
  // Normalize user concerns (expand aliases)
  const expandedConcerns = new Set<string>();
  for (const concern of userConcerns) {
    const normalized = concern.toLowerCase().trim();
    expandedConcerns.add(normalized);
    
    // Check if this concern has aliases, add the canonical form
    for (const [canonical, aliases] of Object.entries(CONCERN_ALIASES)) {
      if (aliases.some(a => a.toLowerCase() === normalized) || canonical === normalized) {
        expandedConcerns.add(canonical);
        aliases.forEach(a => expandedConcerns.add(a.toLowerCase()));
      }
    }
  }
  
  // Check each product ingredient
  for (const ingredient of productIngredients) {
    const normalizedIngredient = ingredient.toLowerCase().trim();
    const ingredientData = INGREDIENT_CONCERN_MAP[normalizedIngredient];
    
    if (!ingredientData) continue;
    
    // Find matching concerns
    const matchedConcerns = ingredientData.concerns.filter(c => 
      expandedConcerns.has(c.toLowerCase())
    );
    
    // Also check Korean terms
    const matchedKorean = ingredientData.koreanTerms.filter(k =>
      Array.from(expandedConcerns).some(c => c.includes(k) || k.includes(c))
    );
    
    if (matchedConcerns.length > 0 || matchedKorean.length > 0) {
      const allMatched = [...new Set([...matchedConcerns, ...matchedKorean])];
      matchedIngredients.push({
        ingredient: normalizedIngredient,
        matchedConcerns: allMatched,
        efficacy: ingredientData.efficacy
      });
      
      // Calculate boost based on efficacy
      const efficacyMultiplier = 
        ingredientData.efficacy === 'high' ? 0.15 :
        ingredientData.efficacy === 'medium' ? 0.10 : 0.05;
      
      totalBoost += efficacyMultiplier * Math.min(allMatched.length, 2); // Cap at 2 matches per ingredient
    }
  }
  
  // Cap total boost at 0.5 (50% additional score)
  return {
    scoreBoost: Math.min(totalBoost, 0.5),
    matchedIngredients
  };
}

/**
 * Korean translations for common ingredient names
 */
const INGREDIENT_KO: Record<string, string> = {
  'retinol': '레티놀',
  'niacinamide': '나이아신아마이드',
  'hyaluronic acid': '히알루론산',
  'vitamin c': '비타민 C',
  'ascorbic acid': '비타민 C',
  'salicylic acid': '살리실산',
  'glycolic acid': '글리콜산',
  'centella': '시카',
  'centella asiatica': '시카',
  'ceramide': '세라마이드',
  'peptide': '펩타이드',
  'tea tree': '티트리',
  'snail mucin': '달팽이 무신',
  'propolis': '프로폴리스',
  'aloe': '알로에',
  'green tea': '녹차',
  'squalane': '스쿼란',
  'bakuchiol': '바쿠치올',
  'zinc': '아연',
  'azelaic acid': '아젤라산',
};

/**
 * Korean translations for concern keywords
 */
const CONCERN_KO: Record<string, string> = {
  'acne': '트러블',
  'pores': '모공',
  'wrinkles': '주름',
  'fine lines': '잔주름',
  'hydration': '수분',
  'dryness': '건조함',
  'brightening': '미백',
  'dark spots': '잡티',
  'pigmentation': '색소침착',
  'redness': '붉은기',
  'sensitivity': '민감성',
  'oiliness': '번들거림',
  'aging': '노화',
  'firmness': '탄력',
  'barrier': '장벽',
  'texture': '피부결',
  'blackheads': '블랙헤드',
  'dark circles': '다크서클',
  'sagging': '처짐',
};

/**
 * Translate ingredient name to Korean
 */
function translateIngredient(name: string): string {
  const lower = name.toLowerCase();
  return INGREDIENT_KO[lower] || name;
}

/**
 * Translate concern to Korean
 */
function translateConcern(concern: string): string {
  const lower = concern.toLowerCase();
  return CONCERN_KO[lower] || concern;
}

/**
 * Generates a detailed reason for why a product matches based on ingredients.
 * 
 * @param matchedIngredients - Array of matched ingredients with their concerns
 * @param language - 'en' or 'ko'
 * @returns Human-readable explanation string
 */
export function generateIngredientReason(
  matchedIngredients: Array<{ ingredient: string; matchedConcerns: string[]; efficacy: string }>,
  language: string = 'ko'
): string {
  if (matchedIngredients.length === 0) return '';
  
  // Sort by efficacy (high first)
  const sorted = [...matchedIngredients].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.efficacy as keyof typeof order] - order[b.efficacy as keyof typeof order];
  });
  
  // Take top 2 ingredients
  const top = sorted.slice(0, 2);
  
  if (language === 'ko') {
    // Emotional ingredient benefit phrases
    const ingredientBenefits: Record<string, string> = {
      'retinol': '주름 개선에 탁월한',
      'niacinamide': '모공과 트러블 케어에 효과적인',
      'hyaluronic acid': '수분을 꽉 채워주는',
      'vitamin c': '환한 피부톤을 위한',
      'ascorbic acid': '환한 피부톤을 위한',
      'salicylic acid': '모공 속 깊은 클렌징',
      'centella': '민감 피부 진정에 딱인',
      'ceramide': '피부 장벽 강화에 좋은',
      'peptide': '탄력 있는 피부를 위한',
      'tea tree': '트러블 진정에 효과적인',
      'snail mucin': '재생과 보습에 도움되는',
      'propolis': '영양 공급과 진정에 좋은',
      'squalane': '부드러운 보습막 형성',
      'bakuchiol': '순한 주름 케어',
    };
    
    const parts = top.map(m => {
      const ingredientLower = m.ingredient.toLowerCase();
      const benefit = ingredientBenefits[ingredientLower];
      if (benefit) {
        return `${translateIngredient(m.ingredient)} - ${benefit}`;
      }
      // Fallback: translate concerns
      const concernsKo = m.matchedConcerns.slice(0, 2).map(translateConcern).join(', ');
      return `${translateIngredient(m.ingredient)}이(가) ${concernsKo}에 효과적`;
    });
    return parts.join(', ');
  } else {
    const parts = top.map(m => {
      const concerns = m.matchedConcerns.slice(0, 2).join(' & ');
      return `${m.ingredient} targets ${concerns}`;
    });
    return parts.join('; ') + '.';
  }
}

// ============================================================================
// Routine Logic
// ============================================================================

/**
 * Defines the standard skincare routine order.
 * Lower numbers = earlier in routine.
 */
export const ROUTINE_ORDER: Record<string, number> = {
  'makeup-remover': 1,
  'cleanser': 2,
  'toner': 3,
  'essence': 4,
  'serum': 5,
  'mask': 6,
  'moisturizer': 7,
  'sunscreen': 8,
};

/**
 * Calculates routine-based score adjustments.
 * If user has products in certain steps, boost products in adjacent/missing steps.
 * 
 * @param productCategory - Category of the product being scored
 * @param userSelectedCategories - Categories user has already selected/bought
 * @returns Score adjustment (-0.1 to +0.2)
 */
export function calculateRoutineScore(
  productCategory: string,
  userSelectedCategories: string[]
): number {
  const productOrder = ROUTINE_ORDER[productCategory.toLowerCase()];
  if (!productOrder) return 0;
  
  if (userSelectedCategories.length === 0) {
    // No selections yet - boost essentials (cleanser, moisturizer, sunscreen)
    if (['cleanser', 'moisturizer', 'sunscreen'].includes(productCategory.toLowerCase())) {
      return 0.1;
    }
    return 0;
  }
  
  // Check if user already has this category
  if (userSelectedCategories.some(c => c.toLowerCase() === productCategory.toLowerCase())) {
    return -0.1; // Slight penalty for duplicate category
  }
  
  // Boost adjacent routine steps
  const userOrders = userSelectedCategories
    .map(c => ROUTINE_ORDER[c.toLowerCase()])
    .filter(Boolean);
  
  const hasAdjacent = userOrders.some(o => Math.abs(o - productOrder) === 1);
  if (hasAdjacent) {
    return 0.15; // Boost products that complete the routine
  }
  
  return 0.05; // Small boost for filling gaps
}

/**
 * Suggests complete routine based on user's current selections.
 * 
 * @param currentCategories - Categories user has already selected
 * @returns Array of missing categories in routine order
 */
export function suggestMissingRoutineSteps(currentCategories: string[]): string[] {
  const essential = ['cleanser', 'moisturizer', 'sunscreen'];
  const nice = ['toner', 'serum', 'essence'];
  
  const current = new Set(currentCategories.map(c => c.toLowerCase()));
  
  const missingEssential = essential.filter(c => !current.has(c));
  const missingNice = nice.filter(c => !current.has(c));
  
  // Return essentials first, then nice-to-haves
  return [...missingEssential, ...missingNice];
}

/**
 * Recommends specific product categories based on user concerns.
 * Maps concerns to the most effective product types.
 */
const CONCERN_CATEGORY_MAP: Record<string, string[]> = {
  'acne': ['cleanser', 'serum', 'toner'],
  'pimples': ['cleanser', 'serum', 'toner'],
  'aging': ['serum', 'essence', 'moisturizer'],
  'wrinkles': ['serum', 'essence', 'moisturizer'],
  'fine-lines': ['serum', 'essence', 'moisturizer'],
  'dark-spots': ['serum', 'essence'],
  'hyperpigmentation': ['serum', 'essence', 'toner'],
  'dryness': ['moisturizer', 'essence', 'toner'],
  'dehydration': ['essence', 'toner', 'moisturizer'],
  'oiliness': ['toner', 'cleanser', 'serum'],
  'large-pores': ['toner', 'serum', 'cleanser'],
  'redness': ['serum', 'moisturizer', 'essence'],
  'sensitivity': ['moisturizer', 'cleanser', 'essence'],
  'dullness': ['serum', 'essence', 'toner'],
  'uneven-texture': ['serum', 'toner', 'essence'],
  'sun-damage': ['serum', 'sunscreen', 'essence'],
};

/**
 * Advanced routine scoring that considers user's concerns.
 * Products in categories that address user's concerns get extra boost.
 * 
 * @param productCategory - Category of product being scored
 * @param userConcerns - User's skin concerns
 * @param userCategories - Categories user already owns
 * @returns Score boost (0 to 0.25)
 */
export function calculateConcernRoutineBoost(
  productCategory: string,
  userConcerns: string[],
  userCategories: string[]
): number {
  const category = productCategory.toLowerCase();
  
  // Skip if user already has this category
  if (userCategories.some(c => c.toLowerCase() === category)) {
    return 0;
  }
  
  let boost = 0;
  
  for (const concern of userConcerns) {
    const concernLower = concern.toLowerCase();
    const recommendedCategories = CONCERN_CATEGORY_MAP[concernLower] || [];
    
    if (recommendedCategories.includes(category)) {
      // Higher boost if it's the primary (first) recommended category
      const position = recommendedCategories.indexOf(category);
      if (position === 0) {
        boost += 0.15; // Primary category for this concern
      } else if (position === 1) {
        boost += 0.1;  // Secondary category
      } else {
        boost += 0.05; // Tertiary category
      }
    }
  }
  
  // Cap the boost at 0.25
  return Math.min(boost, 0.25);
}

// ============================================================================
// Seasonal Context
// ============================================================================

/**
 * Seasonal skincare recommendations.
 * Maps seasons to prioritized concerns and product types.
 */
export const SEASONAL_PRIORITIES: Record<string, {
  boostConcerns: string[];
  boostCategories: string[];
  boostIngredients: string[];
  description: string;
}> = {
  winter: {
    boostConcerns: ['dryness', 'dehydration', 'barrier', 'sensitivity', 'redness'],
    boostCategories: ['moisturizer', 'essence', 'oil'],
    boostIngredients: ['ceramides', 'squalane', 'hyaluronic acid', 'panthenol', 'shea butter'],
    description: '겨울철 건조한 피부를 위한 보습 강화 추천',
  },
  spring: {
    boostConcerns: ['sensitivity', 'redness', 'allergies', 'barrier'],
    boostCategories: ['cleanser', 'toner', 'serum'],
    boostIngredients: ['centella', 'panthenol', 'allantoin', 'green tea'],
    description: '봄철 환절기 민감해진 피부를 위한 진정 케어',
  },
  summer: {
    boostConcerns: ['oiliness', 'acne', 'pores', 'sun damage', 'dullness'],
    boostCategories: ['sunscreen', 'toner', 'cleanser'],
    boostIngredients: ['niacinamide', 'salicylic acid', 'vitamin c', 'tea tree'],
    description: '여름철 피지 조절과 자외선 차단 강화',
  },
  fall: {
    boostConcerns: ['dullness', 'uneven tone', 'texture', 'hydration'],
    boostCategories: ['serum', 'essence', 'moisturizer'],
    boostIngredients: ['vitamin c', 'retinol', 'aha', 'hyaluronic acid'],
    description: '가을철 손상된 피부 회복과 톤 개선',
  },
};

/**
 * Get current season based on month (Northern Hemisphere, Korea-centric)
 */
export function getCurrentSeason(): 'winter' | 'spring' | 'summer' | 'fall' {
  const month = new Date().getMonth() + 1; // 1-12
  
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter'; // Dec, Jan, Feb
}

/**
 * Calculate seasonal score boost for a product.
 * 
 * @param productCategory - Product category
 * @param productIngredients - Product's key ingredients
 * @param productConcerns - Concerns the product addresses
 * @param season - Optional season override (defaults to current)
 * @returns Score boost (0 to 0.15)
 */
export function calculateSeasonalBoost(
  productCategory: string,
  productIngredients: string[],
  productConcerns: string[],
  season?: string
): { boost: number; reason: string | null } {
  const currentSeason = season || getCurrentSeason();
  const seasonData = SEASONAL_PRIORITIES[currentSeason];
  
  if (!seasonData) {
    return { boost: 0, reason: null };
  }
  
  let boost = 0;
  const categoryLower = productCategory.toLowerCase();
  const ingredientsLower = productIngredients.map(i => i.toLowerCase());
  const concernsLower = productConcerns.map(c => c.toLowerCase());
  
  // Category match (+0.05)
  if (seasonData.boostCategories.includes(categoryLower)) {
    boost += 0.05;
  }
  
  // Ingredient match (+0.03 per match, max +0.06)
  const matchedIngredients = ingredientsLower.filter(i => 
    seasonData.boostIngredients.some(bi => i.includes(bi) || bi.includes(i))
  );
  boost += Math.min(matchedIngredients.length * 0.03, 0.06);
  
  // Concern match (+0.02 per match, max +0.04)
  const matchedConcerns = concernsLower.filter(c =>
    seasonData.boostConcerns.some(bc => c.includes(bc) || bc.includes(c))
  );
  boost += Math.min(matchedConcerns.length * 0.02, 0.04);
  
  // Cap at 0.15
  boost = Math.min(boost, 0.15);
  
  return {
    boost,
    reason: boost > 0 ? seasonData.description : null,
  };
}

// ============================================================================
// Age-Based Context
// ============================================================================

/**
 * Age group definitions with skincare priorities.
 */
export const AGE_GROUP_PRIORITIES: Record<string, {
  ageRange: [number, number];
  boostConcerns: string[];
  boostIngredients: string[];
  avoidIngredients: string[];
  description: string;
}> = {
  teen: {
    ageRange: [13, 19],
    boostConcerns: ['acne', 'oiliness', 'pores', 'blackheads'],
    boostIngredients: ['salicylic acid', 'niacinamide', 'tea tree', 'zinc'],
    avoidIngredients: ['retinol', 'glycolic acid'], // Too strong for young skin
    description: '청소년 피부를 위한 트러블 케어',
  },
  twenties: {
    ageRange: [20, 29],
    boostConcerns: ['hydration', 'prevention', 'brightening', 'acne'],
    boostIngredients: ['hyaluronic acid', 'vitamin c', 'niacinamide', 'sunscreen'],
    avoidIngredients: [],
    description: '20대 피부 예방 관리와 수분 케어',
  },
  thirties: {
    ageRange: [30, 39],
    boostConcerns: ['fine lines', 'dark spots', 'firmness', 'hydration'],
    boostIngredients: ['retinol', 'vitamin c', 'peptides', 'hyaluronic acid'],
    avoidIngredients: [],
    description: '30대 초기 노화 예방과 탄력 케어',
  },
  forties: {
    ageRange: [40, 49],
    boostConcerns: ['wrinkles', 'sagging', 'dark spots', 'dryness'],
    boostIngredients: ['retinol', 'peptides', 'ceramides', 'niacinamide'],
    avoidIngredients: [],
    description: '40대 안티에이징과 탄력 강화',
  },
  fiftyPlus: {
    ageRange: [50, 100],
    boostConcerns: ['wrinkles', 'sagging', 'dryness', 'barrier', 'dark spots'],
    boostIngredients: ['retinol', 'peptides', 'ceramides', 'squalane', 'bakuchiol'],
    avoidIngredients: [],
    description: '50대+ 깊은 보습과 탄력 집중 케어',
  },
};

/**
 * Get age group from age number
 */
export function getAgeGroup(age: number): string | null {
  for (const [group, data] of Object.entries(AGE_GROUP_PRIORITIES)) {
    if (age >= data.ageRange[0] && age <= data.ageRange[1]) {
      return group;
    }
  }
  return null;
}

/**
 * Calculate age-based score adjustment for a product.
 * 
 * @param userAge - User's age
 * @param productIngredients - Product's key ingredients
 * @param productConcerns - Concerns the product addresses
 * @returns Score adjustment (-0.1 to +0.15) and reason
 */
export function calculateAgeBoost(
  userAge: number | undefined,
  productIngredients: string[],
  productConcerns: string[]
): { boost: number; reason: string | null; warning: string | null } {
  if (!userAge || userAge < 13) {
    return { boost: 0, reason: null, warning: null };
  }
  
  const ageGroup = getAgeGroup(userAge);
  if (!ageGroup) {
    return { boost: 0, reason: null, warning: null };
  }
  
  const ageData = AGE_GROUP_PRIORITIES[ageGroup];
  const ingredientsLower = productIngredients.map(i => i.toLowerCase());
  const concernsLower = productConcerns.map(c => c.toLowerCase());
  
  let boost = 0;
  let warning: string | null = null;
  
  // Check for ingredients to avoid for this age group (-0.1)
  const hasAvoidIngredient = ingredientsLower.some(i =>
    ageData.avoidIngredients.some(ai => i.includes(ai))
  );
  if (hasAvoidIngredient) {
    boost -= 0.1;
    warning = ageGroup === 'teen' 
      ? '이 성분은 어린 피부에 자극적일 수 있습니다'
      : '이 성분은 이 연령대에 권장되지 않습니다';
  }
  
  // Ingredient match (+0.03 per match, max +0.09)
  const matchedIngredients = ingredientsLower.filter(i =>
    ageData.boostIngredients.some(bi => i.includes(bi) || bi.includes(i))
  );
  boost += Math.min(matchedIngredients.length * 0.03, 0.09);
  
  // Concern match (+0.02 per match, max +0.06)
  const matchedConcerns = concernsLower.filter(c =>
    ageData.boostConcerns.some(bc => c.includes(bc) || bc.includes(c))
  );
  boost += Math.min(matchedConcerns.length * 0.02, 0.06);
  
  // Cap boost (but allow negative for warnings)
  boost = Math.max(Math.min(boost, 0.15), -0.1);
  
  return {
    boost,
    reason: boost > 0 ? ageData.description : null,
    warning,
  };
}

export default {
  INGREDIENT_CONCERN_MAP,
  CONCERN_ALIASES,
  ROUTINE_ORDER,
  SEASONAL_PRIORITIES,
  AGE_GROUP_PRIORITIES,
  calculateIngredientScore,
  generateIngredientReason,
  calculateRoutineScore,
  suggestMissingRoutineSteps,
  calculateConcernRoutineBoost,
  getCurrentSeason,
  calculateSeasonalBoost,
  getAgeGroup,
  calculateAgeBoost,
};
